import { useState } from "react";
import { toast } from "sonner";
import { PageHero, SectionHeading, Eyebrow } from "@/components/site/Sections";
import { CtaButton } from "@/components/site/CtaButton";
import { Reveal } from "@/components/site/Reveal";
import { Wizard } from "@/components/site/Wizard";
import {
  StepGrid, StepTitle, Text, Money, Num, Select, Area, Chips, SystemsGrid, UploadStub,
} from "@/components/site/WizardControls";
import { submitWholesale } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { CheckCircle2, FileSearch, Calculator, Users, Megaphone, Handshake, Camera, Banknote } from "lucide-react";

const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily", "Condo", "Townhouse", "Mobile Home", "Mixed Use", "Commercial", "Land", "Other"];
const ROLES = ["Wholesaler", "Investor", "Acquisition Company", "Agent / Broker", "Deal Sourcer", "Other"];
const OCC = ["Vacant", "Owner Occupied", "Tenant Occupied", "Unknown"];
const REHAB = ["Move-In Ready", "Cosmetic Rehab", "Medium Rehab", "Heavy Rehab", "Full Gut Renovation", "Structural / Major Project", "Unknown"];
const YN = ["Yes", "No"];
const YNU = ["Yes", "No", "Unknown"];
const SYSTEMS = ["Roof", "Foundation", "Electrical", "Plumbing", "HVAC", "Windows", "Kitchen", "Bathrooms", "Flooring", "Basement", "Exterior"];

const SERVICES = [
  { icon: FileSearch, title: "Deal Submission", desc: "Submit a wholesale or off-market opportunity for review." },
  { icon: Calculator, title: "Deal Analysis", desc: "We organize contract price, asking price, comps, ARV, rehab and buyer margin." },
  { icon: Users, title: "Buyer Matching", desc: "Match approved opportunities with buyers whose acquisition criteria fit." },
  { icon: Megaphone, title: "Disposition Support", desc: "Deal packaging, buyer outreach, proof-of-funds and offer collection." },
  { icon: Handshake, title: "Transaction Coordination", desc: "Track contract, assignment, earnest money, title, closing and status." },
  { icon: Banknote, title: "Financing Support", desc: "Connect interested buyers to the Vertexhub financing workflow when requested." },
];

const PROCESS = ["Submit", "Analyze", "Match", "Market", "Close"];

export default function Wholesale() {
  const [f, setF] = useState({ occupancy: "Unknown", under_contract: "No", assignable: "Unknown", equitable_interest: "Not Sure", property_access: "Yes", rehab_level: "Unknown", systems: {} });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setSys = (item, val) => setF((p) => ({ ...p, systems: { ...p.systems, [item]: val } }));

  const onSubmit = async () => {
    if (!f.first_name || !f.last_name || !f.phone || !f.email || !f.property_address) {
      toast.error("Please complete name, contact and property address.");
      return;
    }
    setSending(true);
    try {
      const res = await submitWholesale(f);
      setDone(res);
      toast.success("Wholesale deal submitted for review.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const steps = [
    { key: "Contact", title: "Contact Information", render: () => (
      <StepGrid>
        <StepTitle hint="Tell us who you are and your role.">Your Details</StepTitle>
        <Text label="First Name" value={f.first_name} onChange={set("first_name")} required testid="wh-first-name" />
        <Text label="Last Name" value={f.last_name} onChange={set("last_name")} required testid="wh-last-name" />
        <Text label="Company" value={f.company} onChange={set("company")} />
        <Select label="Role" value={f.role} onChange={set("role")} options={ROLES} testid="wh-role" />
        <Text label="Phone" type="tel" value={f.phone} onChange={set("phone")} required testid="wh-phone" />
        <Text label="Email" type="email" value={f.email} onChange={set("email")} required testid="wh-email" />
      </StepGrid>
    )},
    { key: "Property", title: "Property Information", render: () => (
      <StepGrid>
        <StepTitle>Property</StepTitle>
        <Text label="Property Address" value={f.property_address} onChange={set("property_address")} required full testid="wh-address" />
        <Text label="City" value={f.city} onChange={set("city")} />
        <Text label="County" value={f.county} onChange={set("county")} />
        <Text label="State" value={f.state} onChange={set("state")} />
        <Text label="ZIP Code" value={f.zip_code} onChange={set("zip_code")} />
        <Text label="Parcel Number (optional)" value={f.parcel_number} onChange={set("parcel_number")} />
        <Select label="Property Type" value={f.property_type} onChange={set("property_type")} options={PROP_TYPES} testid="wh-prop-type" />
        <Num label="Bedrooms" value={f.bedrooms} onChange={set("bedrooms")} />
        <Num label="Bathrooms" value={f.bathrooms} onChange={set("bathrooms")} />
        <Num label="Square Feet" value={f.square_feet} onChange={set("square_feet")} />
        <Text label="Lot Size (optional)" value={f.lot_size} onChange={set("lot_size")} />
        <Num label="Year Built (optional)" value={f.year_built} onChange={set("year_built")} />
        <Chips label="Occupancy" options={OCC} value={f.occupancy} onChange={set("occupancy")} testid="wh-occupancy" />
      </StepGrid>
    )},
    { key: "Deal", title: "Deal Information", render: () => (
      <StepGrid>
        <StepTitle hint="Numbers help us underwrite and match buyers faster.">The Numbers</StepTitle>
        <Money label="Contract Price" value={f.contract_price} onChange={set("contract_price")} testid="wh-contract-price" />
        <Money label="Asking Price" value={f.asking_price} onChange={set("asking_price")} testid="wh-asking-price" />
        <Money label="Estimated ARV" value={f.estimated_arv} onChange={set("estimated_arv")} testid="wh-arv" />
        <Money label="Estimated Rehab" value={f.estimated_rehab} onChange={set("estimated_rehab")} testid="wh-rehab" />
        <Money label="Desired Assignment / Transaction Amount" value={f.desired_amount} onChange={set("desired_amount")} />
        <Money label="Earnest Money Deposit" value={f.earnest_money} onChange={set("earnest_money")} optional />
        <Text label="Contract Date" type="date" value={f.contract_date} onChange={set("contract_date")} />
        <Text label="Closing Date" type="date" value={f.closing_date} onChange={set("closing_date")} />
        <Text label="Due Diligence Deadline" type="date" value={f.dd_deadline} onChange={set("dd_deadline")} />
        <Text label="Title Company / Closing Attorney (optional)" value={f.title_company} onChange={set("title_company")} full />
        <Chips label="Is the property currently under contract?" options={YN} value={f.under_contract} onChange={set("under_contract")} />
        <Chips label="Is the contract assignable?" options={YNU} value={f.assignable} onChange={set("assignable")} />
        <Chips label="Do you have contractual or equitable interest?" options={["Yes", "No", "Not Sure"]} value={f.equitable_interest} onChange={set("equitable_interest")} />
        <Chips label="Property access available?" options={["Yes", "No", "Limited"]} value={f.property_access} onChange={set("property_access")} />
      </StepGrid>
    )},
    { key: "Condition", title: "Property Condition", render: () => (
      <StepGrid>
        <StepTitle>Condition</StepTitle>
        <Chips label="Overall rehab level" options={REHAB} value={f.rehab_level} onChange={set("rehab_level")} testid="wh-rehab-level" />
        <SystemsGrid label="Condition of key systems" items={SYSTEMS} value={f.systems} onChange={setSys} testid="wh-systems" />
      </StepGrid>
    )},
    { key: "Docs", title: "Documents & Notes", render: () => (
      <StepGrid>
        <StepTitle hint="Upload the purchase agreement, assignment, photos, comps or rehab estimate.">Documents</StepTitle>
        <UploadStub label="Upload Documents (optional)" testid="wh-files" onNote={() => toast.info("Files noted. A coordinator will request secure uploads.")} />
        <Area label="Notes" value={f.notes} onChange={set("notes")} placeholder="Anything else we should know about this deal…" testid="wh-notes" />
      </StepGrid>
    )},
  ];

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Submitted" title="Deal Submitted for Review." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            {typeof done.deal_score === "number" && (
              <div className="mx-auto mt-6 inline-flex flex-col items-center rounded-2xl border border-gold/40 bg-[#FAF8F1] px-8 py-5">
                <span className="font-mono-x text-xs uppercase tracking-widest text-gold">Vertexhub Deal Score</span>
                <span className="font-display text-5xl font-bold text-[#0B132B]">{done.deal_score}</span>
                <span className="text-sm text-slate-500">{done.score_band}</span>
              </div>
            )}
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Your wholesale opportunity has been submitted to Vertexhub Ventures for review. Submission does not
              guarantee acceptance, buyer placement, financing, sale or closing.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="Wholesale Real Estate Services"
        title="Wholesale Real Estate Services."
        subtitle="Have a deal? Vertexhub Ventures can help organize the opportunity, analyze the numbers, connect it with qualified buyers, and coordinate the transaction through closing."
        image={IMAGES.facade}
      >
        <div className="flex flex-wrap gap-3">
          <CtaButton href="#submit" size="lg">Submit a Wholesale Deal</CtaButton>
          <CtaButton to="/buy" size="lg" variant="outline">Join Buyer Network</CtaButton>
        </div>
      </PageHero>

      {/* process */}
      <section className="border-b border-slate-200 py-14">
        <div className="mx-auto max-w-4xl px-4 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
            {PROCESS.map((p, i) => (
              <div key={p} className="flex items-center gap-2 sm:gap-4">
                <span className="rounded-full border border-gold/40 bg-[#FAF8F1] px-4 py-2 font-mono-x text-xs uppercase tracking-widest text-gold">{p}</span>
                {i < PROCESS.length - 1 && <span className="text-slate-300">→</span>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* services */}
      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <SectionHeading eyebrow="What We Do" title="Disposition, Done Right." light={false} />
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 60}>
                <div className="h-full rounded-xl border border-slate-200 bg-white p-7 shadow-sm">
                  <div className="inline-flex rounded-lg border border-gold/30 bg-gold/5 p-3"><s.icon className="h-5 w-5 text-gold" /></div>
                  <h3 className="mt-4 text-base font-semibold text-[#0B132B]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* form */}
      <section id="submit" className="border-t border-slate-200 bg-[#F5F3EC] py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <SectionHeading center eyebrow="Submit a Wholesale Deal" title="Submit Your Deal for Review" light={false} />
          <div className="mt-10">
            <Wizard steps={steps} onSubmit={onSubmit} submitting={sending} submitLabel="Submit Deal for Review" testid="wholesale-deal-form" />
          </div>
        </div>
      </section>
    </div>
  );
}
